import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import type { DashboardDto } from '@genoma/shared';

import { Centered } from '../../components/Centered.tsx';
import { useI18n } from '../../i18n/index.tsx';
import { fetchDashboard } from '../../services/dashboard.service.ts';
import { AlertList } from './AlertList.tsx';

/**
 * Every operational alert, once three of them stop being enough.
 *
 * The dashboard shows the first three because a column of near-identical
 * warnings taller than everything beside it is a list nobody finishes reading.
 * They still all matter, so they live here rather than nowhere — and they come
 * from the same dashboard payload, because a second endpoint returning the
 * same alerts computed a second way is how two screens start disagreeing.
 */
export const AlertsPage = () => {
  const { t } = useI18n();
  const [data, setData] = useState<DashboardDto | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    fetchDashboard()
      .then(setData)
      .catch((caught: unknown) =>
        setError(caught instanceof Error ? caught.message : t('errors.generic')),
      );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (error) return <Centered>{error}</Centered>;
  if (!data) return <Centered>{t('common.loading')}</Centered>;

  return (
    <div className="mx-auto max-w-3xl px-6 py-8">
      <Link
        to="/panel"
        className="font-mono text-[10px] tracking-[0.12em] text-soft uppercase hover:text-teal"
      >
        ← {t('nav.overview')}
      </Link>

      <header className="mt-3">
        <h1 className="text-[26px] font-extrabold tracking-[-0.025em]">
          {t('dashboard.alertsTitle')}
        </h1>
        <p className="mt-0.5 text-[13.5px] text-soft">{t('dashboard.alertsLead')}</p>
      </header>

      <div className="mt-5">
        <AlertList alerts={data.alerts} />
      </div>

      <p className="mt-4 text-xs text-faint">{t('dashboard.privacyFooter')}</p>
    </div>
  );
};
